/* eslint-disable max-statements */
import DXFWriter from '@tarikjabiri/dxf';
import { type CornerSize, Island, IslandType, Vertex2 } from '@rta/rta-blueprint-library';

import { convertPolyPoints2Vectors } from '@r-core/helpers';
import { type RenderResult } from './types';

import { Layer, type Vector } from '@r-core/types';
import type { RenderDetails } from '@rta/rta-blueprint-library';

type GenerateCountertopVectorsParams = {
	drawing: DXFWriter;
	countertop: ReturnType<Island['getCountertop']>;
	island: Island;
	xOffset: number;
	yOffset: number;
};

/**
 * For corner islands, builds a geometrically correct countertop polygon from the
 * override dimension formulas. The shared renderingDetails uses getSidePoints which
 * has inconsistent 45° miter padding, producing a slightly wrong front edge.
 */
const getCornerCountertopRenderDetails = (island: Island, countertop: ReturnType<Island['getCountertop']>): RenderDetails => {
	const cornerSize = island.size as CornerSize;
	const { frontPanel } = island;
	const panels = island.panels.children;
	const leftSide = frontPanel.getLeftPanel();
	const rightSide = frontPanel.getRightPanel();
	const leftBack = leftSide?.getLeftPanel();
	const rightBack = rightSide?.getRightPanel();

	const backWidth = Math.max(...island.panels.getBackPanels().map((p) => p.size.width));
	const frontOh = frontPanel.countertopOverhang;

	// Compute edge lengths from the same override formulas as the dimension code
	const leftBackLen = backWidth + (leftBack?.getLeftPanel()?.countertopOverhang ?? 0) + (leftBack?.getRightPanel()?.countertopOverhang ?? 0);
	const rightBackLen = backWidth + (rightBack?.getLeftPanel()?.countertopOverhang ?? 0) + (rightBack?.getRightPanel()?.countertopOverhang ?? 0);
	const leftSideLen = cornerSize.depth.left + (leftSide?.getLeftPanel()?.countertopOverhang ?? 0) + frontOh - (leftSide?.countertopOverhang ?? 0);
	const rightSideLen = cornerSize.depth.right + (rightSide?.getRightPanel()?.countertopOverhang ?? 0) + frontOh - (rightSide?.countertopOverhang ?? 0);
	const dx = leftBackLen - rightSideLen;
	const dy = rightBackLen - leftSideLen;
	const frontLen = Math.sqrt(dx * dx + dy * dy);

	// Use the existing renderingDetails to get the correct z-position for the front edge
	const existingVertices = countertop.renderingDetails.vertices;
	const zFront = existingVertices[0].y; // front panel vertex z (from corners[0])

	// Walk the polygon edges using override lengths and panel face directions
	const edgeLengths = [frontLen, leftSideLen, leftBackLen, rightBackLen, rightSideLen];
	const edgeAngles = panels.map((p) => p.getOriginAngle());

	// Start at front-RIGHT vertex, walk counterclockwise
	const verts: Array<{ x: number; y: number }> = [];
	let cx = frontLen / 2;
	let cy = zFront;

	for (let edge = 0; edge < 5; edge++) {
		verts.push({ x: cx, y: cy });
		const panelIdx = edge;
		const len = edgeLengths[panelIdx];
		const angleRad = edgeAngles[panelIdx] * Math.PI / 180;
		cx += len * -Math.cos(-angleRad);
		cy += len * -Math.sin(-angleRad);
	}

	// Reorder: verts = [frontRight(4), frontLeft(0), upperLeft(1), apex(2), upperRight(3)]
	// Panel order: [front(0), leftSide(1), backLeft(2), backRight(3), rightSide(4)]
	const vertices = [
		new Vertex2({ dataIn: { x: verts[1].x, y: verts[1].y }}),
		new Vertex2({ dataIn: { x: verts[2].x, y: verts[2].y }}),
		new Vertex2({ dataIn: { x: verts[3].x, y: verts[3].y }}),
		new Vertex2({ dataIn: { x: verts[4].x, y: verts[4].y }}),
		new Vertex2({ dataIn: { x: verts[0].x, y: verts[0].y }})
	];

	return {
		vertices,
		depth: countertop.material.thickness,
		layer: countertop.instanceOf
	};
};

/**
 * Renders countertop in top view (x, z coordinates)
 * Returns vectors and the width used
 */
export const generateCountertopVectors = (params: GenerateCountertopVectorsParams): RenderResult => {
	const { drawing, countertop, island, xOffset, yOffset } = params;
	const { islandSize } = island;
	let countertopWidth = 0;
	let countertopDepth = 0;

	const vectors: Array<Vector<Vertex2>> = [];
	const labelX = xOffset - ((islandSize.width) / 2);

	// For corner islands, use geometrically correct polygon; for standard, use renderingDetails
	const renderDetails = (island.type === IslandType.Corner && island.panels.children.length === 5)
		? getCornerCountertopRenderDetails(island, countertop)
		: countertop.renderingDetails;
	renderDetails.layer = Layer.Panel;
	const originalVectors = convertPolyPoints2Vectors(renderDetails);
	// const breakpoints: Array<number> = [];

	const min = [0, 0];
	const max = [0, 0];

	// Transform to DXF coordinates: center at xOffset and zOffset
	// Create new vector objects to avoid mutating shared references
	originalVectors.forEach((vector) => {
		const fromX = isFinite(vector.from.x) ? vector.from.x : 0;
		const fromY = isFinite(vector.from.y) ? vector.from.y : 0;
		const toX = isFinite(vector.to.x) ? vector.to.x : 0;
		const toY = isFinite(vector.to.y) ? vector.to.y : 0;

		min[0] = Math.min(min[0], fromX);
		min[1] = Math.min(min[1], fromY);
		max[0] = Math.max(max[0], fromX);
		max[1] = Math.max(max[1], fromY);

		min[0] = Math.min(min[0], toX);
		min[1] = Math.min(min[1], toY);
		max[0] = Math.max(max[0], toX);
		max[1] = Math.max(max[1], toY);

		// Store as plain objects to avoid Vertex2 getter issues
		const newFromX = xOffset + fromX;
		const newFromY = yOffset + fromY;
		const newToX = xOffset + toX;
		const newToY = yOffset + toY;

		// if (countertop.breakpoints.children.length === 0) {
			vectors.push({
				...vector,
				from: new Vertex2({ dataIn: { x: newFromX, y: newFromY }}),
				to: new Vertex2({ dataIn: { x: newToX, y: newToY }})
			});
		// }
	});

	countertopWidth = Math.abs(min[0]) + Math.abs(max[0]);
	countertopDepth = Math.abs(min[1]) + Math.abs(max[1]);
	const frontPanelWidth = island.frontPanel.size.width;

	// Position label below countertop, left side of the front panel (negate y to match DXF coordinate system)
	drawing.setCurrentLayer(Layer.Label);
	drawing.addText(xOffset - (frontPanelWidth / 2), -yOffset - (countertopDepth / 2) - 5, 3, 'countertop');

	return {
		vectors,
		width: countertopWidth,
		height: countertopDepth,
		labelX
	};
};
